import { StateCreator } from "zustand";

import type { SearchStore } from "@/stores/search";
import { SearchDecision, SearchNudgeKind } from "@/types";

// One pass over a result set inside a search session. Changing anything that
// changes the results (filters, tuning, radius) ends the iteration and starts
// the next; the session ending ends it too. Sally mode leaves it untouched.
export interface SearchIteration {
  id: string;
  sessionId: string;
  jobId: string;
  // Identity of the result set — the JSON of the search params. Same key
  // after a remount means the same iteration.
  resultsKey: string;
  startedAtMs: number;
  pagesLoaded: number;
  // Raw results the API returned across loaded pages.
  resultsLoaded: number;
  // What the staffer could actually act on: deduped, minus already-hidden
  // talents. The denominator for hide/interest ratios — pages aren't always
  // full once client-side filtering runs.
  resultsShown: number;
  interestCount: number;
  hideCount: number;
  undoCount: number;
  // Page-break nudges already shown this iteration; a filter change resets.
  shownNudges: SearchNudgeKind[];
}

export interface IterationSliceState {
  iteration: SearchIteration | null;
}

export interface IterationSliceActions {
  // Starts a new iteration unless one is already running for the same session
  // and results — a remount with unchanged filters continues, not restarts.
  beginIteration: (
    sessionId: string,
    jobId: string,
    resultsKey: string,
  ) => void;
  recordIterationDecision: (decision: SearchDecision) => void;
  recordIterationUndo: () => void;
  // Monotonic: a page refetched in place never regresses the counts.
  recordIterationResults: (counts: {
    pagesLoaded: number;
    resultsLoaded: number;
    resultsShown: number;
  }) => void;
  markNudgeShown: (kind: SearchNudgeKind) => void;
  resetIteration: () => void;
}

export type IterationSlice = IterationSliceState & IterationSliceActions;

export const createIterationSlice: StateCreator<
  SearchStore,
  [],
  [],
  IterationSlice
> = (set, get) => ({
  iteration: null,

  beginIteration: (sessionId, jobId, resultsKey) => {
    const current = get().iteration;
    if (
      current &&
      current.sessionId === sessionId &&
      current.resultsKey === resultsKey
    ) {
      return;
    }
    set({
      iteration: {
        id: crypto.randomUUID(),
        sessionId,
        jobId,
        resultsKey,
        startedAtMs: Date.now(),
        pagesLoaded: 0,
        resultsLoaded: 0,
        resultsShown: 0,
        interestCount: 0,
        hideCount: 0,
        undoCount: 0,
        shownNudges: [],
      },
    });
  },

  recordIterationDecision: (decision) => {
    const { iteration } = get();
    if (!iteration) return;
    if (decision === SearchDecision.INTERESTED) {
      set({
        iteration: { ...iteration, interestCount: iteration.interestCount + 1 },
      });
    } else if (decision === SearchDecision.HIDDEN) {
      set({ iteration: { ...iteration, hideCount: iteration.hideCount + 1 } });
    }
  },

  recordIterationUndo: () => {
    const { iteration } = get();
    if (!iteration) return;
    set({ iteration: { ...iteration, undoCount: iteration.undoCount + 1 } });
  },

  recordIterationResults: (counts) => {
    const { iteration } = get();
    if (!iteration) return;
    const next = {
      pagesLoaded: Math.max(iteration.pagesLoaded, counts.pagesLoaded),
      resultsLoaded: Math.max(iteration.resultsLoaded, counts.resultsLoaded),
      resultsShown: Math.max(iteration.resultsShown, counts.resultsShown),
    };
    if (
      next.pagesLoaded === iteration.pagesLoaded &&
      next.resultsLoaded === iteration.resultsLoaded &&
      next.resultsShown === iteration.resultsShown
    ) {
      return;
    }
    set({ iteration: { ...iteration, ...next } });
  },

  markNudgeShown: (kind) => {
    const { iteration } = get();
    if (!iteration || iteration.shownNudges.includes(kind)) return;
    set({
      iteration: {
        ...iteration,
        shownNudges: [...iteration.shownNudges, kind],
      },
    });
  },

  resetIteration: () => {
    set({ iteration: null });
  },
});
